import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Sun,
  Moon,
  Sparkles,
  Camera,
  Palette,
  Wand2,
  ChevronRight,
  Play,
  Shield,
  Zap,
  Users,
  Award,
  TrendingUp,
  Clock,
  Globe2,
  Rocket,
  Quote,
  Upload,
  Building2,
  Sofa,
  Image as ImageIcon,
  Star,
  BadgeCheck,
  Maximize2,
  X,
  Film,
  Video,
  Move,
  RotateCw,
  ArrowLeft,
  Mail,
  Coins
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import packageJson from '../../package.json';
import OffersSection from '../components/OffersSection';

type BeforeAfterSliderProps = {
  beforeSrc: string;
  afterSrc: string;
  altBefore: string;
  altAfter: string;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
};

type ServiceShowcase = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  beforeSrc: string;
  afterSrc: string;
  altBefore: string;
  altAfter: string;
  bullets: string[];
  metric: { value: string; label: string };
  icon: LucideIcon;
  gradient: string;
  accent: string;
  beforeLabel?: string;
  afterLabel?: string;
};

type TrustSignal = {
  icon: LucideIcon;
  label: string;
  description: string;
};

type Highlight = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type WorkflowStep = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type Testimonial = {
  name: string;
  title: string;
  company: string;
  quote: string;
  result: string;
  rating: number;
};

type FAQItem = {
  question: string;
  answer: string;
};

type WhyChooseItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type RoleOutcome = {
  icon: LucideIcon;
  role: string;
  stat: string;
  description: string;
};

type SubscriptionOutcome = {
  icon: LucideIcon;
  value: string;
  label: string;
  description: string;
};

const BeforeAfterSlider: React.FC<BeforeAfterSliderProps> = ({
  beforeSrc,
  afterSrc,
  altBefore,
  altAfter,
  beforeLabel = 'Before',
  afterLabel = 'After',
  className
}) => {
  const [position, setPosition] = useState(52);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState(52);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const activePointerIdRef = useRef<number | null>(null);
  const [isModalDragging, setIsModalDragging] = useState(false);
  const activeModalPointerIdRef = useRef<number | null>(null);

  const clamp = (value: number) => Math.max(0, Math.min(100, value));

  const updatePosition = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const percentage = clamp((relativeX / rect.width) * 100);
    setPosition(percentage);
  };

  const updateModalPosition = (clientX: number) => {
    if (!modalContainerRef.current) return;
    const rect = modalContainerRef.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const percentage = clamp((relativeX / rect.width) * 100);
    setModalPosition(percentage);
  };

  const handleOpenModal = useCallback(() => {
    setModalPosition(position);
    setIsModalOpen(true);
  }, [position]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setPosition(modalPosition);
  }, [modalPosition]);

  useEffect(() => {
    if (!isModalOpen || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseModal();
      }
    };

    const targetWindow = typeof window !== 'undefined' ? window : null;
    targetWindow?.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      targetWindow?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseModal, isModalOpen]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    activePointerIdRef.current = event.pointerId;
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updatePosition(event.clientX);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== event.pointerId && !event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    if (!isDragging) return;
    event.preventDefault();
    updatePosition(event.clientX);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current === event.pointerId) {
      setIsDragging(false);
      activePointerIdRef.current = null;
    }
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPosition(Number(event.target.value));
  };

  const handleModalPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    activeModalPointerIdRef.current = event.pointerId;
    setIsModalDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateModalPosition(event.clientX);
  };

  const handleModalPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activeModalPointerIdRef.current !== event.pointerId && !event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    if (!isModalDragging) return;
    event.preventDefault();
    updateModalPosition(event.clientX);
  };

  const handleModalPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (activeModalPointerIdRef.current === event.pointerId) {
      setIsModalDragging(false);
      activeModalPointerIdRef.current = null;
    }
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleModalRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModalPosition(Number(event.target.value));
    setIsModalDragging(false);
  };

  return (
    <>
      <div className={`relative overflow-hidden rounded-3xl bg-slate-950/90 ${className ?? ''}`}>
        <div
          ref={containerRef}
          className="relative h-full w-full cursor-ew-resize select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{ touchAction: 'pan-y' }}
        >
          <img
            src={afterSrc}
            alt={altAfter || 'AI-enhanced real estate photo'}
            className="absolute inset-0 h-full w-full object-cover brightness-[0.95]"
            loading="lazy"
            width="800"
            height="600"
            fetchPriority="high"
          />

          <div
            className="absolute inset-0 h-full w-full overflow-hidden transition-[clip-path] duration-150 ease-out"
            style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
          >
            <img
              src={beforeSrc}
              alt={altBefore || 'Original real estate photo before AI enhancement'}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
              width="800"
              height="600"
            />
          </div>

          <span className="absolute left-4 top-4 rounded-full bg-slate-900/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-lg backdrop-blur-md">
            {beforeLabel}
          </span>
          <span className="absolute right-4 top-4 rounded-full bg-emerald-500/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-lg backdrop-blur-md">
            {afterLabel}
          </span>

          <button
            type="button"
            onClick={handleOpenModal}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            className="absolute bottom-4 right-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-slate-900/80 text-white shadow-lg backdrop-blur transition hover:bg-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900/30"
            aria-label="Open full-screen preview"
          >
            <Maximize2 className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="pointer-events-none absolute inset-0">
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-white/80 shadow-[0_0_20px_rgba(15,23,42,0.35)] transition-opacity duration-150"
              style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
            >
              <div className="pointer-events-auto absolute top-1/2 left-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_8px_30px_rgba(15,23,42,0.4)] ring-2 ring-blue-500/40">
                <span aria-hidden className="text-xs font-semibold text-slate-700">
                  ⇆
                </span>
              </div>
            </div>
          </div>

          <input
            type="range"
            min={0}
            max={100}
            value={position}
            onChange={handleRangeChange}
            aria-label="Drag to see the transformation"
            className="before-after-slider absolute bottom-4 left-1/2 z-10 w-2/3 -translate-x-1/2 cursor-ew-resize appearance-none bg-transparent focus:outline-none focus-visible:outline-none"
          />

          <div className="pointer-events-none absolute bottom-4 left-1/2 hidden -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white backdrop-blur-md sm:flex">
            Drag to see the transformation
          </div>
        </div>
      </div>
      {isModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/90 px-4 py-8 backdrop-blur"
            onClick={handleCloseModal}
          >
            <div
              className="relative w-full max-w-5xl rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-2xl backdrop-blur-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute right-6 top-6 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white shadow-lg backdrop-blur transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900/60"
                aria-label="Close full-screen preview"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>

              <div className="mb-6 pr-16">
                <h3 className="text-lg font-semibold text-white">
                  Full-screen transformation preview
                </h3>
                <p className="text-sm text-slate-200/80">
                  Drag the slider or use arrow keys to compare the original and enhanced results.
                </p>
              </div>

              <div
                ref={modalContainerRef}
                className="relative h-[60vh] min-h-[360px] w-full cursor-ew-resize select-none overflow-hidden rounded-3xl bg-slate-900/40"
                style={{ touchAction: 'pan-y' }}
                onPointerDown={(event) => {
                  event.preventDefault();
                  handleModalPointerDown(event);
                }}
                onPointerMove={(event) => {
                  event.preventDefault();
                  handleModalPointerMove(event);
                }}
                onPointerUp={(event) => {
                  event.preventDefault();
                  handleModalPointerUp(event);
                }}
                onPointerCancel={handleModalPointerUp}
                onPointerLeave={handleModalPointerUp}
              >

                <img
                  src={afterSrc}
                  alt={altAfter || 'AI-enhanced real estate photo'}
                  className="h-full w-full object-contain"
                  width="1200"
                  height="800"
                />
                <div
                  className="absolute inset-0 h-full w-full overflow-hidden transition-[clip-path] duration-150 ease-out"
                  style={{ clipPath: `inset(0 ${100 - modalPosition}% 0 0)` }}
                >
                  <img
                    src={beforeSrc}
                    alt={altBefore || 'Original real estate photo before AI enhancement'}
                    className="absolute inset-0 h-full w-full object-contain"
                    width="1200"
                    height="800"
                  />
                </div>

                <span className="absolute left-6 top-6 rounded-full bg-slate-900/80 px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg backdrop-blur">
                  {beforeLabel}
                </span>
                <span className="absolute right-6 top-6 rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg backdrop-blur">
                  {afterLabel}
                </span>

                <div className="pointer-events-none absolute inset-0">
                  <div
                    className="absolute top-0 bottom-0 w-[2px] bg-white/80 shadow-[0_0_30px_rgba(15,23,42,0.45)]"
                    style={{ left: `${modalPosition}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className="absolute top-1/2 left-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-[0_8px_35px_rgba(15,23,42,0.45)]">
                      ⇆
                    </div>
                  </div>
                </div>

                <input
                  type="range"
                  min={0}
                  max={100}
                  value={modalPosition}
                  onChange={handleModalRangeChange}
                  aria-label="Drag to compare the transformation"
                  className="before-after-slider absolute bottom-6 left-1/2 z-20 w-1/2 -translate-x-1/2 cursor-ew-resize appearance-none bg-transparent focus:outline-none focus-visible:outline-none"
                />

                <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-4 py-2 text-sm font-medium text-white shadow-lg backdrop-blur">
                  Drag to compare • {Math.round(modalPosition)}% AI enhanced
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

const serviceShowcases: ServiceShowcase[] = [
  {
    id: 'interior-design',
    name: 'Interior Design',
    tagline: 'Stage every room virtually',
    description:
      'Fill empty spaces with on-trend furniture, lighting, and textures generated from curated design systems that convert browsers into showings.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1762543751614_u45vjj_1761275559906_q0fxg6_IMG_4065.webp',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/interior_design_undefined_processed_1762543763594_8ljmmu',
    altBefore: 'Empty dining room before staging',
    altAfter: 'Furnished dining room with modern decor',
    bullets: [
      'Design styles curated by top stagers',
      'Floor-aware furniture placement with correct shadows',
      'Generate multiple looks for each buyer persona'
    ],
    metric: { value: '+85%', label: 'more showing requests' },
    icon: Palette,
    gradient: 'from-purple-500 to-violet-600',
    accent: 'Interior Design',
    beforeLabel: 'Before',
    afterLabel: 'After'
  },
  {
    id: 'replace-elements',
    name: 'Replace Elements',
    tagline: 'Erase distractions, keep realism',
    description:
      'Remove clutter, swap finishes, and fix construction artifacts while preserving architectural accuracy and lighting continuity.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1762879685600_wjch86_1761277140390_nleq3q_image-1761260228284.jpg',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/1761277140390_nleq3q_image-1761260228284_replaced_processed_1762879695149_plkbm7.jpg',
    altBefore: 'Kitchen before unwanted elements are removed',
    altAfter: 'Kitchen after element replacement with clean surfaces',
    bullets: [
      'Mask-free removal with context-aware fill',
      'Swap cabinets, counters, and fixtures instantly',
      'True-to-life reflections and grain preservation'
    ],
    metric: { value: '98%', label: 'perceived realism score' },
    icon: Wand2,
    gradient: 'from-emerald-500 to-teal-500',
    accent: 'Replace Elements',
    beforeLabel: 'Before',
    afterLabel: 'After'
  },
  {
    id: 'add-furnitures',
    name: 'Add Furnitures',
    tagline: 'Turn empty units into aspirational homes',
    description:
      'Place designer-grade furniture, art, and accessories that highlight scale, flow, and lifestyle for every room size and layout.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1762880195635_o416w1_image-1759341837469.jpg',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/image-1759341837469_furnished_processed_1762880214867_fazzdm.jpg',
    altBefore: 'Unfurnished living room before virtual staging',
    altAfter: 'Living room staged with stylish furniture',
    bullets: [
      'Choose modern, transitional, or luxury collections',
      'Automatically matches flooring tones and wall colors',
      'Export print-quality images and 4K web assets'
    ],
    metric: { value: '4.9★', label: 'average buyer rating' },
    icon: Sofa,
    gradient: 'from-amber-500 to-orange-500',
    accent: 'Add Furnitures',
    beforeLabel: 'Before',
    afterLabel: 'After'
  },
  {
    id: 'exterior-design',
    name: 'Exterior Design',
    tagline: 'Refresh curb appeal from the sidewalk view',
    description:
      'Visualize landscaping, siding, and twilight conversions that help buyers picture life in the home before they ever visit.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1761263932418_tpscv6_address-of-the-property-1-26-2048x1365.jpeg.webp',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/1762548704405_a2ev5c_address-of-the-property-1-26-2048x1365.jpeg_exterior_processed_1763677709944_7en8zw.jpg',
    altBefore: 'Exterior home before AI enhancements',
    altAfter: 'Exterior home after landscaping and lighting enhancements',
    bullets: [
      'AI landscaping presets for every climate zone',
      'Golden-hour twilight renders with accurate shadows',
      'Siding and roof recolors that stay architectural'
    ],
    metric: { value: '+62%', label: 'more listing clicks' },
    icon: Building2,
    gradient: 'from-cyan-500 to-sky-500',
    accent: 'Exterior Design',
    beforeLabel: 'Before',
    afterLabel: 'After'
  },
  {
    id: 'enhance-images',
    name: 'Enhance Images',
    tagline: 'Correct lighting & color in seconds',
    description:
      'Transform raw shots into magazine-ready imagery using domain-trained AI built for real estate and interior design teams.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1762879328372_pktlqe_IMG_5685.PNG',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/IMG_5685_enhanced_processed_1762879343417_w0dmzz.png',
    altBefore: 'Dimly lit living room before AI enhancement',
    altAfter: 'Bright, professionally enhanced living room with balanced lighting and improved colors',
    bullets: [
      'AI tone mapping protects natural window views',
      'Batch process entire shoots with one click',
      'Portfolio-ready exports with horizon correction for property tours'
    ],
    metric: { value: '5× faster', label: 'than manual retouching' },
    icon: Camera,
    gradient: 'from-blue-500 to-indigo-500',
    accent: 'Enhance Images',
    beforeLabel: 'Before',
    afterLabel: 'After'
  },
];

const trustSignals: TrustSignal[] = [
  {
    icon: Shield,
    label: 'Enterprise-grade security',
    description: 'SOC2-ready infrastructure and private workspaces keep client photography locked down.'
  },
  {
    icon: Clock,
    label: 'Quick results in 12 seconds',
    description: 'Parallel render pipeline delivers staged and enhanced photos before your coffee cools.'
  },
  {
    icon: BadgeCheck,
    label: 'Quality guarantee',
    description: 'We guarantee all generations match the video and image styles shown. If something is not generated as expected, we\'ll make it right.'
  },
  {
    icon: TrendingUp,
    label: '+72% more qualified leads',
    description: 'Listings featuring RealVision AI visuals outperform traditional photos across leading real estate marketplaces.'
  }
];

const featureHighlights: Highlight[] = [
  {
    icon: Camera,
    title: 'AI-optimized outputs',
    description: 'Automatic straightening, noise reduction, and AI model selection tuned for real estate deliver web + print assets instantly.'
  },
  {
    icon: Wand2,
    title: 'Intelligent scene editing',
    description: 'Granular controls for walls, floors, and fixtures with live previews and undo-safe edits.'
  },
  {
    icon: Globe2,
    title: 'Team-first workspace',
    description: 'Invite photographers, stagers, and coordinators with approval flows, version history, and comments.'
  }
];

const workflowSteps: WorkflowStep[] = [
  {
    icon: Upload,
    title: 'Upload your shoot',
    description: 'Drag & drop RAW, HDR, or smartphone photos. We auto-detect rooms and recommend best styles.'
  },
  {
    icon: Palette,
    title: 'Pick your transformation',
    description: 'Choose enhancement, staging, replacement, furnishing, or exterior presets tailored to your goals.'
  },
  {
    icon: CheckCircle,
    title: 'Approve in seconds',
    description: 'Fine-tune before/after sliders, lock favorites, and request instant variations if needed.'
  },
  {
    icon: TrendingUp,
    title: 'Publish & track',
    description: 'Export market-ready files, share galleries, and monitor engagement with built-in analytics powered by our real estate AI.'
  }
];

const testimonials: Testimonial[] = [
  {
    name: 'Caroline Vega',
    title: 'Top 1% Listing Agent',
    company: 'Compass Dallas',
    quote:
      'Our listings now launch with marketing assets in under an hour. RealVision AI staging consistently delivers the “wow” moment buyers expect.',
    result: 'Booked 3× more showings the first month',
    rating: 5
  },
  {
    name: 'Ashley Patel',
    title: 'Marketing Director',
    company: 'UrbanNest Developments',
    quote:
      'Exterior design previews helped us lock pre-sales faster. Buyers see the future community before the first brick is laid.',
    result: 'Secured 40% more pre-sales reservations',
    rating: 5
  },
  {
    name: 'Devon Hart',
    title: 'Director of Marketing Ops',
    company: 'Harbor & Key Realty',
    quote:
      'RealVision AI’s success team built our templates, trained every agent, and checks in weekly. Support is the reason we transitioned our entire photo program in two weeks.',
    result: 'Onboarded 12 agents and 4 photographers in the first week',
    rating: 4
  }
];

const faqs: FAQItem[] = [
  {
    question: 'How fast can I get enhanced and staged photos back?',
    answer:
      'Most projects finish in under three minutes thanks to our GPU render cluster. You will receive an email notification and can continue editing or requesting instant variations without waiting in a queue.'
  },
  {
    question: 'Do I need design or editing experience?',
    answer:
      'Not at all. RealVision AI was built alongside top photographers and stagers. You choose a goal, preview the before/after slider, and we handle the complex editing under the hood.'
  },
  {
    question: 'What if my generation doesn\'t match the expected quality?',
    answer:
      'We guarantee all generations match the video and image styles shown on our platform. If something is not generated as expected or doesn\'t meet the quality standards demonstrated in our examples, contact our support team and we\'ll regenerate it at no additional cost or provide a credit refund.'
  },
  {
    question: 'Can my team collaborate on the same project?',
    answer:
      'Yes. Invite unlimited teammates, set approvals, leave timestamped comments, and lock versions. Every action is tracked so you stay audit-ready for enterprise clients.'
  },
  {
    question: 'What makes RealVision AI models stand out?',
    answer:
      'Our AI stack blends diffusion, upscaling, and structure-aware models that are trained on real estate and interior design datasets, so your assets stay architecturally accurate while delivering polished, client-ready visuals every time.'
  }
];

const socialProofLogos = ['Compass', 'Keller Williams', 'Coldwell Banker', 'eXp Realty', 'Sotheby’s', 'RE/MAX'];

const heroCheckmarks = [
  'No credit card required to start',
  'Batch process entire photo sets with one click for quick client delivery',
  '5x faster than manual editing, giving you more time for creative work'
];

const whyChooseBullets: WhyChooseItem[] = [
  {
    icon: Sparkles,
    title: 'Specialized real estate AI',
    description: 'Domain-trained models respect architecture, layout, and decor styles across residential and commercial spaces.'
  },
  {
    icon: Shield,
    title: 'Guided onboarding & support',
    description: 'Dedicated success architects configure styles, workflows, and QA reviews so your team ships confidently on day one.'
  },
  {
    icon: Clock,
    title: '12-second production speed',
    description: 'Parallel rendering keeps photographers and marketers ahead of deadlines without sacrificing polish.'
  },
  {
    icon: Users,
    title: 'Built for cross-team collaboration',
    description: 'Invite agents, designers, and developers with approval flows, version history, and comments in a single workspace.'
  }
];

const successMetrics = [
  {
    label: 'Listings sell faster',
    value: '37%',
    description: 'average reduction in days-on-market',
    icon: TrendingUp
  },
  {
    label: 'Buyer engagement',
    value: '+124%',
    description: 'increase in gallery interactions',
    icon: Users
  },
  {
    label: 'Time saved per shoot',
    value: '3.4h',
    description: 'manual editing eliminated',
    icon: Clock
  }
];

const ctaHighlights = [
  'Choose from flexible pricing plans that fit your needs',
  'Unlimited before/after previews to showcase client-ready proofs',
  'Dedicated success architect onboarding your team in 48 hours'
];

const roleOutcomes: RoleOutcome[] = [
  {
    icon: TrendingUp,
    role: 'Realtors',
    stat: '3× more signed listings',
    description: 'Win seller trust with instant AI staging proofs and marketing decks tailored to each property.'
  },
  {
    icon: Users,
    role: 'Designers',
    stat: '60% quicker concept approvals',
    description: 'Prototype multiple interior looks in minutes and lock sourcing-ready mood boards with clients.'
  },
  {
    icon: Award,
    role: 'Agents',
    stat: '+45% premium package upsell',
    description: 'Bundle AI visuals with concierge services to increase commission opportunities and repeat referrals.'
  },
  {
    icon: Camera,
    role: 'Photographers',
    stat: '4h saved per shoot',
    description: 'Automate retouching, twilight conversions, and clutter removal so you can book more sessions weekly.'
  },
  {
    icon: Rocket,
    role: 'Developers',
    stat: '2× faster pre-sales velocity',
    description: 'Render future amenities and exterior transformations that accelerate investor buy-in and buyer reservations.'
  }
];

const subscriptionOutcomes: SubscriptionOutcome[] = [
  {
    icon: TrendingUp,
    value: '+28%',
    label: 'higher premium package attach rate',
    description: 'Convert more sellers to top-tier marketing plans with AI visuals that justify pricing.'
  },
  {
    icon: Users,
    value: '2.5×',
    label: 'increase in qualified prospect demos',
    description: 'Send interactive before/after links that turn curious leads into booked consultations.'
  },
  {
    icon: Shield,
    value: '-35%',
    label: 'reduction in churn across teams',
    description: 'Deliver consistent AI results with onboarding, governance, and success reviews baked into every plan.'
  }
];

const LandingPage: React.FC = () => {
  const [showAuth, setShowAuth] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [posterUrl, setPosterUrl] = useState<string>('');

  useEffect(() => {
    const pageTitle = 'RealVision AI | AI Real Estate Photo Enhancement & Virtual Staging';
    const description =
      'RealVision AI enhances property photos, stages interiors, replaces elements, adds furnitures, and refreshes exteriors with production-ready AI. Transform raw shots into professional visuals in 12 seconds, driving 85% more viewings and 62% more engagement.';
    const keywords = 'AI real estate photo enhancement, virtual staging, property photo editing, real estate AI, interior design AI, exterior design AI, photo enhancement software, real estate marketing, property photography, AI staging, real estate photo retouching, virtual furniture staging, property listing photos';
    const canonicalUrl = typeof window !== 'undefined' ? window.location.href : 'https://realvisionaire.com';
    const ogImage = 'https://realvisionaire.com/logo_black.png';

    // Update title
    document.title = pageTitle;

    // Helper function to set or update meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Primary meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', 'RealVision AI');
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('googlebot', 'index, follow');
    setMetaTag('bingbot', 'index, follow');

    // Open Graph tags
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:title', pageTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', 'RealVision AI - AI-powered real estate photo enhancement and virtual staging', true);
    setMetaTag('og:site_name', 'RealVision AI', true);
    setMetaTag('og:locale', 'en_US', true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:url', canonicalUrl);
    setMetaTag('twitter:title', pageTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:image:alt', 'RealVision AI - AI-powered real estate photo enhancement');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Structured Data (JSON-LD)
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://realvisionaire.com/#organization',
          name: 'RealVision AI',
          url: 'https://realvisionaire.com',
          logo: {
            '@type': 'ImageObject',
            url: 'https://realvisionaire.com/logo_black.png',
            width: 512,
            height: 512
          },
          sameAs: [],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Service',
            email: 'contact@realvisionaire.com'
          }
        },
        {
          '@type': 'SoftwareApplication',
          '@id': 'https://realvisionaire.com/#software',
          name: 'RealVision AI',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            ratingCount: '150',
            bestRating: '5',
            worstRating: '1'
          },
          description: description,
          featureList: [
            'AI-powered photo enhancement',
            'Virtual interior staging',
            'Element replacement',
            'Furniture addition',
            'Exterior design enhancement',
            '12-second processing time',
            'Batch processing',
            'Before/after comparisons'
          ]
        },
        {
          '@type': 'WebPage',
          '@id': 'https://realvisionaire.com/#webpage',
          url: canonicalUrl,
          name: pageTitle,
          description: description,
          inLanguage: 'en-US',
          isPartOf: {
            '@id': 'https://realvisionaire.com/#website'
          },
          about: {
            '@id': 'https://realvisionaire.com/#organization'
          },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: ogImage
          }
        },
        {
          '@type': 'WebSite',
          '@id': 'https://realvisionaire.com/#website',
          url: 'https://realvisionaire.com',
          name: 'RealVision AI',
          description: description,
          publisher: {
            '@id': 'https://realvisionaire.com/#organization'
          },
          inLanguage: 'en-US'
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        },
        ...testimonials.map((testimonial, index) => ({
          '@type': 'Review',
          '@id': `https://realvisionaire.com/#review-${index}`,
          author: {
            '@type': 'Person',
            name: testimonial.name,
            jobTitle: testimonial.title,
            worksFor: {
              '@type': 'Organization',
              name: testimonial.company
            }
          },
          reviewBody: testimonial.quote,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: testimonial.rating,
            bestRating: '5',
            worstRating: '1'
          },
          itemReviewed: {
            '@id': 'https://realvisionaire.com/#software'
          }
        })),
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://realvisionaire.com'
            }
          ]
        }
      ]
    };

    // Remove existing structured data script if any
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Language attribute
    if (document.documentElement) {
      document.documentElement.setAttribute('lang', 'en');
    }

    // Cleanup function
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Request notification permission after page loads
  useEffect(() => {
    // Request notification permission after a short delay to avoid interrupting page load
    const requestNotificationPermission = () => {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              console.log('Notification permission granted');
            } else {
              console.log('Notification permission denied');
            }
          }).catch((error) => {
            console.error('Error requesting notification permission:', error);
          });
        }, 2000); // 2 seconds after page load
      }
    };

    requestNotificationPermission();
  }, []);

  // Capture frame at 3 seconds for video thumbnail
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video loads first
    const handleLoadedData = () => {
      // Video is loaded, now try to capture frame
      const captureFrame = () => {
        try {
          // Only capture if video duration is available and >= 3 seconds
          if (video.duration && video.duration >= 3) {
            video.currentTime = 3;
            const handleSeeked = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 1920;
                canvas.height = video.videoHeight || 1080;
                const ctx = canvas.getContext('2d');
                if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  setPosterUrl(dataUrl);
                  video.poster = dataUrl;
                }
              } catch (error) {
                // CORS error or canvas export failed - silently fail and use no poster
                console.warn('Could not capture video frame for thumbnail:', error);
              }
            };
            video.addEventListener('seeked', handleSeeked, { once: true });
          }
        } catch (error) {
          console.warn('Could not seek video for thumbnail capture:', error);
        }
      };

      // Wait a bit for video to be ready
      if (video.readyState >= 2) {
        captureFrame();
      } else {
        video.addEventListener('loadedmetadata', captureFrame, { once: true });
      }
    };

    // Handle video load errors
    const handleError = () => {
      console.warn('Video failed to load');
    };

    if (video.readyState >= 4) {
      // Video is already loaded
      handleLoadedData();
    } else {
      video.addEventListener('loadeddata', handleLoadedData, { once: true });
      video.addEventListener('error', handleError, { once: true });
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadedmetadata', handleLoadedData);
    };
  }, []);

  const scrollToAuth = () => {
    setShowAuth(true);
    requestAnimationFrame(() => {
      document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleAnchorNavigation = (anchorId: string) => {
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const heroService = {
    id: 'enhance-images',
    name: 'Main highlight',
    tagline: 'Correct lighting & color in seconds',
    description:
      'Transform raw shots into magazine-ready imagery using domain-trained AI built for real estate and interior design teams.',
    beforeSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1761279323790_yh1571_1761275559906_q0fxg6_IMG_4065.webp',
    afterSrc: 'https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/uploads/1761277455358_inxnid_image-1761260228284.jpg',
    altBefore: 'Dimly lit living room before AI enhancement',
    altAfter: 'Bright, professionally enhanced living room with balanced lighting and improved colors',
    bullets: [
      'AI tone mapping protects natural window views',
      'Batch process entire shoots with one click',
      'Portfolio-ready exports with horizon correction for property tours'
    ],
    metric: { value: '5× faster', label: 'than manual retouching' },
    icon: Camera,
    gradient: 'from-blue-500 to-indigo-500',
    accent: 'Main highlight'
  };

  const GetStartedCTA = () => (<div className="text-center">
    <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white dark:bg-slate-900 rounded-2xl border-2 border-purple-200 dark:border-purple-800 p-6 shadow-lg">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-indigo-500 to-pink-500 rounded-full flex items-center justify-center">
          <Rocket className="h-6 w-6 text-white" />
        </div>
        <div className="text-left">
          <div className="font-bold text-slate-900 dark:text-white">Get Started Today</div>
          <div className="text-sm text-slate-600 dark:text-slate-300">Choose a plan that works for you</div>
        </div>
      </div>
      <button
        onClick={scrollToAuth}
        className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 px-6 py-3 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
      >
        <span>Get Started Now</span>
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  </div>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 transition-colors duration-300 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <header>
        <nav
          className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/60 bg-white/80 backdrop-blur-lg transition-colors duration-300 dark:border-slate-800/70 dark:bg-slate-950/80"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <a href="/" aria-label="RealVision AI Home">
                <img
                  src={theme === 'dark' ? '/logo_white.png' : '/logo_black.png'}
                  alt="RealVision AI - AI-powered real estate photo enhancement and virtual staging platform"
                  className="h-12 w-auto sm:h-14"
                  width="140"
                  height="56"
                  loading="eager"
                />
              </a>
            </div>
            <div className="hidden items-center space-x-6 text-sm font-medium text-slate-600 dark:text-slate-300 lg:flex">
              <button onClick={() => handleAnchorNavigation('features-section')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                Features
              </button>
              <button onClick={() => handleAnchorNavigation('services-section')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                Services
              </button>
              <button onClick={() => handleAnchorNavigation('showcase-section')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                Before &amp; After
              </button>
              <button onClick={() => handleAnchorNavigation('testimonials-section')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                Proof
              </button>
              <button onClick={() => handleAnchorNavigation('faq-section')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                FAQ
              </button>
              <button onClick={() => navigate('/pricing')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                Pricing
              </button>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="hidden items-center rounded-full border border-amber-200/80 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-600 shadow-sm dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300 sm:flex">
                Limited pricing
              </div>
              <button
                onClick={toggleTheme}
                className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-all hover:border-blue-400 hover:text-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-blue-500"
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
              <button
                onClick={scrollToAuth}
                className="flex items-center space-x-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/40 sm:px-6 sm:text-base"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </nav>
      </header>



      <main role="main">

        {/* Priority Section: Smart Effects & Video Features */}
        <section
          className="relative px-4 py-20 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 dark:from-purple-950/30 dark:via-indigo-950/30 dark:to-pink-950/30 overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300/20 rounded-full blur-3xl"></div>
          </div>

          <div className="relative mx-auto max-w-7xl">
            {/* Main H1 Headline */}
            <div className="text-center mb-20">
              <div className="inline-flex items-center space-x-2 rounded-full border border-purple-200/70 bg-white/70 px-4 py-2 text-sm font-semibold uppercase tracking-wider text-purple-700 shadow-sm backdrop-blur dark:border-purple-500/40 dark:bg-purple-500/15 dark:text-purple-200 mb-8">
                <Sparkles className="h-4 w-4" />
                <span>New & Exclusive</span>
              </div>
              <h1 className="mt-6 text-3xl font-bold leading-tight text-slate-900 dark:text-white sm:text-5xl sm:leading-[1.05] lg:text-6xl">
                You Found{' '}
                <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 bg-clip-text text-transparent drop-shadow-lg">
                  The Platform
                </span>
                <br />
                That Generates{' '}
                <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent drop-shadow-lg">
                  The Best Videos
                </span>
                {' '}for Real Estate Professionals
              </h1>
              <p className="text-xl sm:text-2xl text-slate-700 dark:text-slate-300 max-w-4xl mx-auto font-medium">
                Transform your property listings with{' '}
                <span className="text-purple-600 dark:text-purple-400 font-semibold">AI-powered video generation</span>
                {' '}that creates{' '}
                <span className="text-orange-600 dark:text-orange-400 font-semibold">captivating, professional content</span>
                {' '}in seconds
              </p>
            </div>

            {/* Smart Effects - Two Side-by-Side Video Players */}
            <div className="mb-16">
              <div className="grid gap-8 lg:grid-cols-2">
                {/* Smart Effects Player 1 - Helicopter */}
                <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-blue-200 dark:border-blue-800 p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  {/* Gradient overlay badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Header */}
                  <div className="mb-6 relative z-10">
                    <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-orange-100 to-pink-100 dark:from-orange-900/30 dark:to-pink-900/30 mb-3">
                      <span className="text-2xl">🎆🎈🎊</span>
                      <span className="text-sm font-semibold text-orange-700 dark:text-pink-300">
                        Fireworks, Balloons &amp; Confetti
                      </span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      Spectacular Celebration Combo
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 text-sm lg:text-base">
                      Dazzle your listings with a stunning showcase of fireworks, festive balloons, and bursts of confetti—all in one magical effect to truly celebrate your property.
                    </p>
                  </div>

                  {/* Video Player */}
                  <div className="mb-6 rounded-xl overflow-hidden border-2 border-blue-200 dark:border-blue-800 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/50 dark:to-cyan-950/50 relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-0 pointer-events-none"></div>
                    <video
                      className="w-full h-auto relative z-10"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster=""
                    >
                      <source
                        src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/combined-effects.mp4"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Quick Generation</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">HD Quality</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">No Watermarks</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Instant Download</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <button
                    onClick={() => navigate('/smart-effects')}
                    className="w-full group/btn relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:from-blue-700 hover:via-cyan-700 hover:to-blue-700"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Experience the Helicopter Flyover</span>
                      <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </div>

                {/* Smart Effects Player 2 - Snow */}
                <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-cyan-200 dark:border-cyan-800 p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  {/* Gradient overlay badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-200 rounded-full flex items-center justify-center shadow-lg">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Header */}
                  <div className="mb-6 relative z-10">
                    <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30 mb-3">
                      <span className="text-2xl">❄️</span>
                      <span className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">Snowfall Wonder</span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      Snowfall Wonder Effect
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 text-sm lg:text-base">
                      Transform your scene with soft, mesmerizing snow for a cozy, winter atmosphere that truly delights.
                    </p>
                  </div>

                  {/* Video Player */}
                  <div className="mb-6 rounded-xl overflow-hidden border-2 border-cyan-200 dark:border-cyan-800 shadow-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/50 dark:to-blue-950/50 relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-0 pointer-events-none"></div>
                    <video
                      className="w-full h-auto relative z-10"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster=""
                    >
                      <source
                        src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/snow.mp4"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Quick Generation</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">HD Quality</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">No Watermarks</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Instant Download</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <button
                    onClick={() => navigate('/smart-effects')}
                    className="w-full group/btn relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:from-cyan-700 hover:via-blue-700 hover:to-cyan-700"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Add Magical Snowfall Now</span>
                      <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </div>
              </div>
              <div className="grid gap-8 lg:grid-cols-2 mt-10">
                {/* Smart Effects Player 3 - Balloons */}
                <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-purple-200 dark:border-purple-800 p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  {/* Gradient overlay badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Header */}
                  <div className="mb-6 relative z-10">
                    <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 mb-3">
                      <span className="text-2xl">🎈</span>
                      <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">Balloons Celebration</span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      Balloons Celebration Effect
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 text-sm lg:text-base">
                      Watch as thousands of colorful balloons transform your property into an unforgettable moment.
                    </p>
                  </div>

                  {/* Video Player */}
                  <div className="mb-6 rounded-xl overflow-hidden border-2 border-purple-200 dark:border-purple-800 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/50 dark:to-pink-950/50 relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-0 pointer-events-none"></div>
                    <video
                      className="w-full h-auto relative z-10"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster=""
                    >
                      <source
                        src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/image-1763237393203_effect_balloons_processed_1763422131221_fyrpv1_video_processed_1763435904340_jl6co1.mp4"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Quick Generation</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">HD Quality</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">No Watermarks</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Instant Download</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <button
                    onClick={() => navigate('/smart-effects')}
                    className="w-full group/btn relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:from-purple-700 hover:via-pink-700 hover:to-purple-700"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Create Amazing Balloons Effect Now</span>
                      <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </div>

                {/* Smart Effects Player 4 - Sunrise */}
                <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-orange-200 dark:border-orange-800 p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                  {/* Gradient overlay badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Header */}
                  <div className="mb-6 relative z-10">
                    <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 mb-3">
                      <span className="text-2xl">🌅</span>
                      <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Sunrise Magic</span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white mb-2">
                      Sunrise Magic Effect
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 text-sm lg:text-base">
                      Experience the breathtaking beauty of a golden sunrise that highlights every feature and creates an emotional connection.
                    </p>
                  </div>

                  {/* Video Player */}
                  <div className="mb-6 rounded-xl overflow-hidden border-2 border-orange-200 dark:border-orange-800 shadow-lg bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/50 dark:to-amber-950/50 relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent z-0 pointer-events-none"></div>
                    <video
                      className="w-full h-auto relative z-10"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster=""
                    >
                      <source
                        src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/image-1763237393203_effect_sunrise_processed_1763422286611_y37pmz_video_processed_1763514922759_0t0bee.mp4"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Quick Generation</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">HD Quality</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">No Watermarks</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">Instant Download</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <button
                    onClick={() => navigate('/smart-effects')}
                    className="w-full group/btn relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:from-orange-700 hover:via-amber-700 hover:to-orange-700"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Transform with Sunrise Magic</span>
                      <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </div>
              </div>

              <div className="my-10">

                <GetStartedCTA />

              </div>
              
              {/* Quality Guarantee Section */}
              <section
                className="relative px-4 py-16 sm:px-6 lg:px-8 overflow-hidden"
              >
                {/* Background decoration */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-purple-500/10 blur-3xl"></div>
                </div>

                <div className="relative mx-auto max-w-5xl">
                  <div className="rounded-3xl border-2 border-emerald-200/70 dark:border-emerald-500/40 bg-gradient-to-br from-emerald-50/90 via-white to-blue-50/90 dark:from-emerald-950/30 dark:via-slate-900 dark:to-blue-950/30 p-8 sm:p-12 shadow-2xl backdrop-blur">
                    {/* Badge */}
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex items-center space-x-2 rounded-full border-2 border-emerald-500/50 bg-emerald-500/10 dark:bg-emerald-500/20 px-5 py-2 text-sm font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 shadow-lg">
                        <Shield className="h-5 w-5" />
                        <span>100% Quality Guarantee</span>
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="text-center">
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
                        <span className="bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                          We Guarantee All Generations Match our video and image styles
                        </span>
                      </h2>
                      <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
                        Not as expected? <span className="font-bold text-emerald-600 dark:text-emerald-400 underline decoration-2 decoration-emerald-500">We'll make it right.</span>
                      </p>

                      {/* Features Grid */}
                      <div className="grid sm:grid-cols-3 gap-4 mt-10">
                        <div className="flex flex-col items-center p-4 rounded-xl bg-white/60 dark:bg-slate-800/60 border border-emerald-200/50 dark:border-emerald-500/30">
                          <div className="w-12 h-12 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center mb-3">
                            <BadgeCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">Style Match</h3>
                          <p className="text-xs text-slate-600 dark:text-slate-300 text-center">All outputs match our demonstrated quality</p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-xl bg-white/60 dark:bg-slate-800/60 border border-blue-200/50 dark:border-blue-500/30">
                          <div className="w-12 h-12 rounded-full bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center mb-3">
                            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">We Fix It</h3>
                          <p className="text-xs text-slate-600 dark:text-slate-300 text-center">Not satisfied? We'll regenerate or refund</p>
                        </div>
                        <div className="flex flex-col items-center p-4 rounded-xl bg-white/60 dark:bg-slate-800/60 border border-purple-200/50 dark:border-purple-500/30">
                          <div className="w-12 h-12 rounded-full bg-purple-500/10 dark:bg-purple-500/20 flex items-center justify-center mb-3">
                            <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">No Risk</h3>
                          <p className="text-xs text-slate-600 dark:text-slate-300 text-center">100% satisfaction guaranteed on every generation</p>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-10">
                        <button
                          onClick={scrollToAuth}
                          className="inline-flex items-center space-x-2 rounded-xl bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/40"
                        >
                          <Shield className="h-5 w-5" />
                          <span>Get Started with Confidence</span>
                          <ArrowRight className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Middle Section with Previous Headline */}
              <div className="text-center my-16 py-12 border-t-2 border-b-2 border-purple-200 dark:border-purple-800">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
                  Bring Your Listings to Life with
                  <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 bg-clip-text text-transparent">
                    {' '}AI Magic & Motion
                  </span>
                </h2>
                <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                  Transform static photos into captivating experiences with smart effects, cinematic camera movements, and animated scenes that make properties unforgettable.
                </p>
              </div>

              {/* Additional Info Below Players */}
              <div className="mt-8 text-center">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  <span className="font-semibold text-purple-600 dark:text-purple-400">Amazing Effects Available:</span>
                  {' '}Dusk • Balloons • Helicopter • Gift Bow • Fireworks • Confetti • Holiday Lights • Snow • Sunrise
                </p>
                <button
                  onClick={() => navigate('/smart-effects')}
                  className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg border-2 border-purple-300 dark:border-purple-700 bg-white dark:bg-slate-900 text-purple-700 dark:text-purple-300 font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200"
                >
                  <span>Explore All Smart Effects</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Three Feature Cards */}
            <div className="grid gap-8 lg:grid-cols-3 mb-12">

              {/* Camera Movement (Drone Effect) Card */}
              <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-indigo-200 dark:border-indigo-800 p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="absolute top-4 right-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <Film className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    🎥 Drone Effect
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    Add cinematic camera movements to your photos. Create professional drone-like footage from static images.
                  </p>
                </div>
                {/* Video Player */}
                <div className="mb-6 rounded-xl overflow-hidden border border-indigo-200 dark:border-indigo-800 shadow-lg">
                  <video
                    className="w-full h-auto"
                    controls
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster=""
                  >
                    <source
                      src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/image-1763237393203_effect_sunrise_processed_1763422286611_y37pmz_video_processed_1763514922759_0t0bee.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="space-y-3 mb-6">
                  {[
                    { icon: ArrowLeft, name: 'Truck Left', desc: 'Smooth left movement' },
                    { icon: ArrowRight, name: 'Pan Right', desc: 'Reveal more scene' },
                    { icon: Move, name: 'Tracking Shot', desc: 'Follow subject motion' },
                    { icon: RotateCw, name: 'Bullet Time', desc: 'Dramatic orbit effect' }
                  ].map((movement) => {
                    const Icon = movement.icon;
                    return (
                      <div
                        key={movement.name}
                        className="flex items-center space-x-3 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-800"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-slate-900 dark:text-white text-sm">{movement.name}</div>
                          <div className="text-xs text-slate-600 dark:text-slate-300">{movement.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    <span>4 professional camera movements</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    <span>1080p video output</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    <span>6 second clips</span>
                  </li>
                </ul>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">Cinematic Quality</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">2-5 min</span>
                  </div>
                </div>
              </div>

              {/* Smart Effects Card */}
              <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-orange-200 dark:border-orange-800 p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="absolute top-4 right-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    ✨ Smart Effects
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    Instantly apply different professional effects, from Dusk and Sunrise to Fireworks, Holiday Lights, and more. Highlight your property and wow your clients.
                  </p>
                </div>
                {/* Effect Images Gallery */}
                <div className="mb-6 space-y-6">
                  {[
                    { src: "https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/confetti.jpg", alt: "Confetti Effect" },
                    { src: "https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/fireworks.jpg", alt: "Fireworks Effect" },
                    { src: "https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/helicopter.jpg", alt: "Helicopter Effect" },
                    { src: "https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/sample_effects/holiday-lights.jpg", alt: "Holiday Lights Effect" },
                  ].map((effect, i) => (
                    <div key={effect.alt} className="relative group overflow-hidden rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10 shadow-lg hover:shadow-2xl transition-all duration-300">
                      <img
                        src={effect.src}
                        alt={effect.alt}
                        className="w-full h-auto object-cover rounded-xl"
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-white text-base font-semibold text-center drop-shadow-lg">{effect.alt}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span>Unique effects including Dusk, Balloons, Fireworks & more</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span>1-click enhancement & download</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span>Works on any property photo</span>
                  </li>
                </ul>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-orange-600 dark:text-orange-300">Instant Results</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">~12 sec</span>
                  </div>
                </div>
              </div>

              {/* Animate Scene Card */}
              <div className="group relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-pink-200 dark:border-pink-800 p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="absolute top-4 right-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg">
                    <Video className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    🎬 Animate Scene
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    Bring static property photos to life with AI-generated animations. Create engaging video content from any image.
                  </p>
                </div>
                {/* Video Player */}
                <div className="mb-6 rounded-xl overflow-hidden border border-pink-200 dark:border-pink-800 shadow-lg">
                  <video
                    className="w-full h-auto"
                    controls
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster=""
                  >
                    <source
                      src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/IMG_5685_enhanced_processed_1762880781139_9fnbn5_video_processed_1763508341250_2231el.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border border-pink-200 dark:border-pink-800">
                  <div className="flex items-center space-x-3 mb-3">
                    <Zap className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                    <span className="font-semibold text-slate-900 dark:text-white">AI-Powered Animation</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Transform any enhanced or staged image into a dynamic video with natural motion, perfect for social media and marketing.
                  </p>
                </div>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 mb-6">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-pink-500 flex-shrink-0" />
                    <span>Natural scene animation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-pink-500 flex-shrink-0" />
                    <span>16:9 or 9:16 aspect ratios</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-pink-500 flex-shrink-0" />
                    <span>Ready for social sharing</span>
                  </li>
                </ul>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">Video Generation</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">2-5 min</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <GetStartedCTA />
          </div>
        </section>

        {/* How It Works - Vertical Timeline */}
        <section
          className="relative px-4 py-20 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/30 dark:via-indigo-950/30 dark:to-purple-950/30"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-64 h-64 bg-blue-300/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-64 h-64 bg-purple-300/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative mx-auto max-w-4xl">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 rounded-full border border-blue-200/70 bg-white/70 px-4 py-2 text-sm font-semibold uppercase tracking-wider text-blue-700 shadow-sm backdrop-blur dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-200 mb-6">
                <Sparkles className="h-4 w-4" />
                <span>Simple Process</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                How It Works
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                Get started in just a few simple steps and transform your property photos with AI-powered magic
              </p>
            </div>

            {/* Vertical Timeline */}
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 via-indigo-400 to-purple-400 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500 hidden md:block"></div>

              {/* Step 1: Add Credits */}
              <div className="relative flex items-start gap-6 mb-12">
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white dark:ring-slate-900">
                    <Coins className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 w-16 h-16 bg-blue-400 rounded-full animate-ping opacity-20"></div>
                </div>
                <div className="flex-1 pt-2">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-blue-200 dark:border-blue-800 p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 font-bold text-sm">1</span>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">Add Credits</h3>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300">
                      Purchase credits to get started. Choose from flexible pricing plans that fit your needs. Credits are used for all AI transformations and effects.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2: Choose Service */}
              <div className="relative flex items-start gap-6 mb-12">
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white dark:ring-slate-900">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 w-16 h-16 bg-indigo-400 rounded-full animate-ping opacity-20"></div>
                </div>
                <div className="flex-1 pt-2">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-full text-indigo-600 dark:text-indigo-400 font-bold text-sm">2</span>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">Choose Smart Effect or Other Service</h3>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 mb-3">
                      Select from our range of AI-powered services: Smart Effects (fireworks, balloons, holiday lights), Interior Design, Exterior Design, Image Enhancement, or Add Furniture.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {['Smart Effects', 'Interior Design', 'Exterior Design', 'Image Enhancement'].map((service) => (
                        <span key={service} className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Upload Photo */}
              <div className="relative flex items-start gap-6 mb-12">
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white dark:ring-slate-900">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 w-16 h-16 bg-purple-400 rounded-full animate-ping opacity-20"></div>
                </div>
                <div className="flex-1 pt-2">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-purple-200 dark:border-purple-800 p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-600 dark:text-purple-400 font-bold text-sm">3</span>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">Upload Your Photo and DONE</h3>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300">
                      Simply upload your property photo. Our AI will process it and generate your enhanced image in just 12 seconds. Download your professional-quality result instantly.
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                      <CheckCircle className="h-5 w-5" />
                      <span>Processing takes just 12 seconds</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Choose Animation */}
              <div className="relative flex items-start gap-6">
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white dark:ring-slate-900">
                    <Video className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 w-16 h-16 bg-pink-400 rounded-full animate-ping opacity-20"></div>
                </div>
                <div className="flex-1 pt-2">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-pink-200 dark:border-pink-800 p-6 shadow-lg hover:shadow-xl transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-full text-pink-600 dark:text-pink-400 font-bold text-sm">4</span>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">Choose Animate or Drone Effect</h3>
                    </div>
                    <p className="text-slate-600 dark:text-slate-300 mb-4">
                      Once your image is ready, bring it to life! Choose between two powerful animation options:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <h4 className="font-semibold text-slate-900 dark:text-white">Animate Scene</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Add natural motion to your scene with dynamic elements that engage viewers.
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-cyan-200 dark:border-cyan-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Camera className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          <h4 className="font-semibold text-slate-900 dark:text-white">Drone Effect</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Apply cinematic camera movements - pan, zoom, and orbit for professional drone-like footage.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-12 text-center">
              <button
                onClick={scrollToAuth}
                className="inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/40"
              >
                <Rocket className="h-5 w-5" />
                <span>Get Started Now</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>



        <section
          className="px-4 pt-24 sm:px-6 sm:pt-28 lg:px-8 lg:pt-32"
          aria-labelledby="hero-heading"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mb-4">
              <div className="inline-flex items-center space-x-2 rounded-full border border-blue-200/70 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700 shadow-sm backdrop-blur dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-200 sm:text-sm">
                <Sparkles className="h-4 w-4" />
                <span>AI-Powered Imagery for Real Estate & Design</span>
              </div>
              <h2
                id="hero-heading"
                className="mt-6 text-3xl font-bold leading-tight text-slate-900 dark:text-white sm:text-5xl sm:leading-[1.05] lg:text-6xl"
              >
                AI Videos and Images
                <span className="bg-gradient-to-r from-emerald-500 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {' '}That Creates Listings That Captivate Buyers
                </span>
              </h2>

              {/* Video and Before/After Slider */}
              <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] lg:items-start">
                {/* Video Player */}
                <div className="order-1 lg:order-1">
                  <div className="rounded-2xl overflow-hidden border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/70">
                    <video
                      ref={videoRef}
                      className="w-full h-auto"
                      controls
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      poster={posterUrl}
                    >
                      <source
                        src="https://pub-b2fab8efcfed441092b0dc6d69b534a9.r2.dev/processed/WhatsApp%20Video%202025-12-02%20at%207.07.47%20PM.mp4"
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>

                {/* Before/After Slider */}
                <div className="relative order-2 lg:order-2">
                  <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-tr from-blue-500/30 via-indigo-500/20 to-purple-500/30 blur-3xl"></div>
                  <div className="rounded-3xl border border-white/40 bg-white/80 p-4 shadow-2xl backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/70">
                    <BeforeAfterSlider
                      beforeSrc={heroService.beforeSrc}
                      afterSrc={heroService.afterSrc}
                      altBefore={heroService.altBefore}
                      altAfter={heroService.altAfter}
                      beforeLabel="Before"
                      afterLabel="After"
                      className="h-64 sm:h-72 md:h-80"
                    />
                    <div className="mt-5 grid  rounded-2xl border border-blue-100 bg-blue-50/60 p-4 text-sm text-slate-700 shadow dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-100">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase tracking-wide text-blue-500 dark:text-blue-300">Stunning results in 12 seconds</span>
                        <span className="text-xl font-semibold text-slate-900 dark:text-white">Balanced lighting, preserved room and calibrated colors without manual masking.</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAnchorNavigation('showcase-section')}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-blue-600 bg-white px-6 py-3 text-sm font-semibold text-blue-600 shadow-sm transition-all hover:bg-blue-50 hover:shadow-md dark:border-blue-400 dark:bg-slate-900 dark:text-blue-400 dark:hover:bg-slate-800 sm:text-base"
                    >
                      <ImageIcon className="h-5 w-5" />
                      <span>View All Transformations</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="mt-6 max-w-3xl text-base text-slate-600 dark:text-slate-300 sm:text-lg md:text-xl">
                Tired of raw videos or photos that don't impress clients or close deals? <strong>RealVision AI</strong> enhances images, stages interiors, replaces elements, adds furnitures, and refreshes exteriors — all while preserving the original structure for authentic results. Transform your shots into professional visuals in 12 seconds, driving 85% more viewings and 62% more engagement.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                <button
                  onClick={scrollToAuth}
                  className="flex w-full items-center justify-center space-x-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-500/35 transition-all hover:-translate-y-0.5 hover:from-blue-700 hover:to-indigo-700 sm:w-auto"
                >
                  <span>Get Started</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleAnchorNavigation('showcase-section')}
                  className="flex w-full items-center justify-center gap-2 text-sm font-medium text-slate-600 underline-offset-4 transition-colors hover:text-blue-600 hover:underline dark:text-slate-300 dark:hover:text-blue-400 sm:w-auto"
                >
                  <Play className="h-4 w-4" />
                  <span>See Live Demos</span>
                </button>
              </div>

              <div className="mt-8 rounded-3xl border border-blue-200/70 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-blue-500/30 dark:bg-slate-900/70">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Why teams choose RealVision AI
                </h3>
                <ul className="mt-4 grid gap-4 sm:grid-cols-2">
                  {whyChooseBullets.map(({ icon: Icon, title, description }) => (
                    <li key={title} className="flex items-start gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
                        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="my-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600/10 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                    <Star className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">FHD quality and ultra realistic results</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Validated real estate & design pros</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Only 12 seconds for full transformation</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">98% AI realism accuracy</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <BadgeCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Quality guarantee</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Generations match our styles or we'll make it right</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Direct Offers Section */}
        <OffersSection />

        {/* Login Form Section - Redirect to /auth page */}
        <section
          id="auth-section"
          className={`px-4 py-12 sm:px-6 lg:px-8 pt-28 sm:pt-32 ${showAuth ? 'scroll-mt-32' : ''}`}
        >
          <div className="mx-auto max-w-md">
            <div className="text-center space-y-6 rounded-2xl bg-white dark:bg-slate-800 p-8 shadow-xl ring-offset-2 ring-offset-white dark:ring-offset-slate-950">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
                Get Started Today
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Sign in or create an account to start enhancing your property photos with AI.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 font-medium text-white transition-all hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span>Go to Login</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>



        <section
          id="features-section"
          aria-labelledby="features-title"
          className="px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <h2
                id="features-title"
                className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl"
              >
                Built to close more listings with less effort
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                Every workflow is co-designed with photographers, stagers, and brokerage teams so you ship premium visuals without bottlenecks.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {featureHighlights.map(({ icon: Icon, title, description }) => (
                <article
                  key={title}
                  className="group flex flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30 transition group-hover:scale-105">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{title}</h3>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{description}</p>
                  <button
                    onClick={() => handleAnchorNavigation('showcase-section')}
                    className="mt-6 inline-flex items-center text-sm font-semibold text-blue-600 transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Explore transformations
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </article>
              ))}
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {trustSignals.map(({ icon: Icon, label, description }) => (
                <div
                  key={label}
                  className="rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900/70"
                >
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${label === 'Quality guarantee'
                    ? 'bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-300'
                    : 'bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300'
                    }`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">{label}</h4>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="services-section"
          aria-labelledby="services-title"
          className="px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl space-y-16">
            <div className="mx-auto max-w-3xl text-center">
              <h2
                id="services-title"
                className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl"
              >
                Five services, one seamless AI studio
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                Give clients precise before &amp; after proof for every transformation. Mix and match services across an entire listing in minutes.
              </p>
            </div>

            <div id="showcase-section" className="space-y-16">
              {serviceShowcases.map(
                ({
                  id,
                  name,
                  tagline,
                  description,
                  beforeSrc,
                  afterSrc,
                  altBefore,
                  altAfter,
                  bullets,
                  metric,
                  icon: Icon,
                  gradient,
                  accent,
                  beforeLabel,
                  afterLabel
                }) => (
                  <article
                    key={id}
                    className="grid gap-10 rounded-3xl border border-slate-200/80 bg-white/90 p-6 shadow-xl shadow-slate-900/5 backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/70 lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)]"
                    itemScope
                    itemType="https://schema.org/Service"
                  >
                    <div className="flex flex-col justify-center space-y-5">
                      <div className="inline-flex items-center space-x-3">
                        <span
                          className={`inline-flex items-center space-x-2 rounded-full bg-gradient-to-r ${gradient} px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{accent}</span>
                        </span>
                      </div>
                      <h3 className="text-2xl font-semibold text-slate-900 dark:text-white sm:text-3xl" itemProp="name">{name}</h3>
                      <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400" itemProp="slogan">
                        {tagline}
                      </p>
                      <p className="text-base text-slate-600 dark:text-slate-300" itemProp="description">{description}</p>
                      <ul className="grid gap-3 text-sm text-slate-600 dark:text-slate-300">
                        {bullets.map((bullet) => (
                          <li key={bullet} className="flex items-center space-x-3">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/50 dark:text-slate-200">
                        <TrendingUp className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                        <span className="text-lg font-bold text-slate-900 dark:text-white">{metric.value}</span>
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {metric.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-4">
                      <BeforeAfterSlider
                        beforeSrc={beforeSrc}
                        afterSrc={afterSrc}
                        altBefore={altBefore}
                        altAfter={altAfter}
                        beforeLabel={beforeLabel}
                        afterLabel={afterLabel}
                        className="h-72 sm:h-80 md:h-[420px]"
                      />
                      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white/70 p-4 text-xs text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300 sm:grid-cols-2">
                        <div className="flex items-center space-x-2">
                          <ImageIcon className="h-4 w-4 text-blue-500 dark:text-blue-300" />
                          <span>Interactive slider ready for listing presentations &amp; client pitches</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Shield className="h-4 w-4 text-emerald-500 dark:text-emerald-300" />
                          <span>Reality anchored, and validated by human QA specialists</span>
                        </div>
                        <div className="flex items-center space-x-2 sm:col-span-2">
                          <BadgeCheck className="h-4 w-4 text-emerald-500 dark:text-emerald-300 flex-shrink-0" />
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">Quality Guarantee:</span>
                          <span>We guarantee all generations match our video and image styles. Not as expected? We'll make it right.</span>
                        </div>
                      </div>
                    </div>
                  </article>
                )
              )}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-xl dark:border-slate-800 dark:bg-slate-900">
              <span className="inline-flex items-center space-x-2 rounded-full bg-emerald-100 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                <Award className="h-4 w-4" />
                <span>Signature case study</span>
              </span>
              <h3 className="mt-6 text-3xl font-semibold text-slate-900 dark:text-white">
                Compass Dallas launched a seven-figure listing in 47 minutes
              </h3>
              <p className="mt-4 text-base text-slate-600 dark:text-slate-300">
                Caroline’s team replaced three vendors and now launches listings the same afternoon. RealVision AI gives sellers tangible before/after proof during the first appointment.
              </p>
              <ul className="mt-6 grid gap-3 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-start space-x-3">
                  <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                    <CheckCircle className="h-4 w-4" />
                  </div>
                  <span>Secured a seven-figure listing by presenting AI staging proofs live.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                    <CheckCircle className="h-4 w-4" />
                  </div>
                  <span>Reduced post-production costs by 64% while increasing listing volume per photographer.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                    <CheckCircle className="h-4 w-4" />
                  </div>
                  <span>Delivered before/after QR codes for buyer journeys and agent follow-ups.</span>
                </li>
              </ul>
            </div>
            <div className="grid gap-6 sm:grid-cols-3">
              {successMetrics.map(({ label, value, description, icon: Icon }) => (
                <div
                  key={label}
                  className="flex flex-col rounded-3xl border border-slate-200 bg-white/80 p-6 text-center shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/70"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="mt-4 text-3xl font-semibold text-slate-900 dark:text-white">{value}</span>
                  <span className="mt-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {label}
                  </span>
                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">
                Role-based results you can forecast
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                Whether you sell, shoot, design, or build, RealVision AI subscriptions tie directly to KPIs that matter for each discipline.
              </p>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {roleOutcomes.map(({ icon: Icon, role, stat, description }) => (
                <div
                  key={role}
                  className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900/80"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-300">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">{role}</h3>
                  <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                    {stat}
                  </p>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl rounded-3xl border border-emerald-200/70 bg-white/90 p-10 shadow-xl dark:border-emerald-500/30 dark:bg-slate-900/80">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
              <div>
                <span className="inline-flex items-center space-x-2 rounded-full bg-emerald-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
                  <TrendingUp className="h-4 w-4" />
                  <span>AI tool outcomes</span>
                </span>
                <h2 className="mt-5 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">
                  Tool that grow revenue, not just image quality
                </h2>
                <p className="mt-4 text-base text-slate-600 dark:text-slate-300">
                  Better images, faster production, and higher close rates. Wait for close rates, faster upsells, and steadier renewals once RealVision AI automates visual production across every listing and project.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {subscriptionOutcomes.map(({ icon: Icon, value, label, description }) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950/80"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-300">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="mt-4 text-2xl font-semibold text-slate-900 dark:text-white">{value}</div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {label}
                    </div>
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl text-white">
            <div className="max-w-2xl">
              <span className="inline-flex items-center space-x-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-blue-200">
                <Sparkles className="h-4 w-4" />
                <span>Launch-ready workflow</span>
              </span>
              <h2 className="mt-6 text-3xl font-semibold sm:text-4xl">Four steps from raw photos to offers</h2>
              <p className="mt-4 text-base text-slate-300">
                Replace manual edits with a conversion-optimized pipeline. Every step was designed alongside brokerage marketing teams.
              </p>
            </div>
            <div className="mt-12 grid gap-6 lg:grid-cols-4">
              {workflowSteps.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="flex flex-col rounded-3xl border border-white/10 bg-white/5 p-6 transition-all hover:-translate-y-1 hover:border-white/20 hover:bg-white/10"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-blue-200">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{title}</h3>
                  <p className="mt-3 text-sm text-slate-200/80">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="testimonials-section"
          aria-labelledby="testimonials-title"
          className="px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <div className="text-center">
              <h2
                id="testimonials-title"
                className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl"
              >
                Social proof that wins skeptical clients
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                Teams across brokerage, development, and marketing switched to RealVision AI to deliver jaw-dropping visuals at scale.
              </p>
            </div>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {testimonials.map(({ name, title, company, quote, result, rating }) => (
                <figure
                  key={name}
                  className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900/80"
                  itemScope
                  itemType="https://schema.org/Review"
                >
                  <Quote className="h-8 w-8 text-blue-500" aria-hidden="true" />
                  <blockquote
                    className="mt-5 flex-1 text-sm text-slate-600 dark:text-slate-300"
                    itemProp="reviewBody"
                  >
                    {quote}
                  </blockquote>
                  <figcaption className="mt-6 border-t border-slate-200 pt-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <div className="font-semibold text-slate-900 dark:text-white" itemProp="author" itemScope itemType="https://schema.org/Person">
                      <span itemProp="name">{name}</span>
                    </div>
                    <div itemProp="author" itemScope itemType="https://schema.org/Person">
                      <span itemProp="jobTitle">{title}</span> · <span itemProp="worksFor" itemScope itemType="https://schema.org/Organization">
                        <span itemProp="name">{company}</span>
                      </span>
                    </div>
                    <div
                      className="mt-2 flex items-center space-x-1 text-amber-400"
                      itemProp="reviewRating"
                      itemScope
                      itemType="https://schema.org/Rating"
                      aria-label={`${rating} out of 5 stars`}
                    >
                      <meta itemProp="ratingValue" content={rating.toString()} />
                      <meta itemProp="bestRating" content="5" />
                      {Array.from({ length: rating }).map((_, index) => (
                        <Star key={index} className="h-4 w-4 fill-current" aria-hidden="true" />
                      ))}
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-blue-500 dark:text-blue-400">
                      {result}
                    </p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section
          id="faq-section"
          aria-labelledby="faq-title"
          className="px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              <h2
                id="faq-title"
                className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl"
              >
                Answers for your launch team
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                Still evaluating? Here are the questions real estate marketers, photographers, and developers ask before switching.
              </p>
            </div>
            <div className="mt-12 space-y-4" itemScope itemType="https://schema.org/FAQPage">
              {faqs.map(({ question, answer }) => (
                <details
                  key={question}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-500/40 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
                  itemScope
                  itemType="https://schema.org/Question"
                >
                  <summary className="flex cursor-pointer items-center justify-between text-left text-lg font-semibold text-slate-900 dark:text-white" itemProp="name">
                    {question}
                    <ChevronRight className="h-5 w-5 text-blue-500 transition-transform duration-200 group-open:rotate-90" aria-hidden="true" />
                  </summary>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <p className="mt-4 text-sm text-slate-600 dark:text-slate-300" itemProp="text">{answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-3xl border border-blue-200/70 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-10 text-white shadow-2xl dark:border-blue-500/40">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center space-x-2 rounded-full bg-white/15 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                <Rocket className="h-4 w-4" />
                <span>Founder launch offer</span>
              </span>
              <h2 className="mt-5 text-3xl font-bold sm:text-4xl">
                Ready to launch market-winning visuals?
              </h2>
              <p className="mt-3 text-base text-blue-100">
                Lock in lifetime pricing, onboard your team in under 48 hours, and start shipping AI-powered before/after assets that win every seller meeting.
              </p>
              <ul className="mt-6 grid gap-3 text-sm text-blue-100">
                {ctaHighlights.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-white">
                      <CheckCircle className="h-4 w-4" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={scrollToAuth}
                className="inline-flex items-center space-x-2 rounded-xl bg-white px-8 py-4 text-base font-semibold text-blue-600 shadow-lg shadow-blue-900/40 transition-all hover:-translate-y-0.5 hover:bg-blue-50"
              >
                Start now
                <ArrowRight className="h-5 w-5" />
              </button>
              <span className="text-xs uppercase tracking-wide text-blue-100/80">
                Start transforming your property photos today
              </span>
            </div>
          </div>
        </div>
      </section>

      <footer
        className="bg-gray-900 py-8 text-white dark:bg-slate-900 sm:py-12"
        role="contentinfo"
        itemScope
        itemType="https://schema.org/WPFooter"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-8">
            <div className="sm:col-span-2 lg:col-span-1" itemScope itemType="https://schema.org/Organization">
              <div className="mb-4 flex items-center space-x-3">
                <img
                  src="/logo_white.png"
                  alt="RealVision AI - AI-powered real estate photo enhancement platform"
                  className="h-6 w-auto sm:h-8"
                  width="140"
                  height="56"
                  itemProp="logo"
                />
                <span className="text-lg font-bold sm:text-xl" itemProp="name">RealVision AI</span>
              </div>
              <p className="text-sm text-gray-400 sm:text-base" itemProp="description">
                Transform your property photos with cutting-edge AI technology designed for modern real estate teams.
              </p>
            </div>
            <nav aria-label="Product navigation">
              <h3 className="mb-3 text-base font-semibold sm:text-lg">Product</h3>
              <ul className="space-y-2 text-sm text-gray-400 sm:text-base">
                <li><a href="#features-section" className="transition-colors hover:text-white" aria-label="View features">Features</a></li>
                <li><a href="#services-section" className="transition-colors hover:text-white" aria-label="View services">Solutions</a></li>
                <li><a href="#showcase-section" className="transition-colors hover:text-white" aria-label="View before and after examples">Before &amp; After</a></li>
              </ul>
            </nav>
            <nav aria-label="Company navigation">
              <h3 className="mb-3 text-base font-semibold sm:text-lg">Company</h3>
              <ul className="space-y-2 text-sm text-gray-400 sm:text-base">
                <li><a href="mailto:contact@realvisionaire.com" className="transition-colors hover:text-white" aria-label="Contact RealVision AI">Contact</a></li>
                <li><a href="#testimonials-section" className="transition-colors hover:text-white" aria-label="View customer testimonials">Customers</a></li>
                <li><a href="#faq-section" className="transition-colors hover:text-white" aria-label="View frequently asked questions">FAQ</a></li>
              </ul>
            </nav>
            <nav aria-label="Resources navigation">
              <h3 className="mb-3 text-base font-semibold sm:text-lg">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-400 sm:text-base">
                <li><a href="#auth-section" className="transition-colors hover:text-white" aria-label="Sign in to RealVision AI">Sign In</a></li>
                <li><a href="/privacy" className="transition-colors hover:text-white" aria-label="Privacy policy">Privacy</a></li>
              </ul>
            </nav>
          </div>
          <div className="mt-6 border-t border-gray-800 pt-6 text-center text-sm text-gray-400 sm:mt-8 sm:pt-8 sm:text-base">
            <p>&copy; 2025 RealVision AI. All rights reserved.</p>
            <small className="mt-1 block text-xs text-gray-500">RealVision AI {packageJson.version}</small>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;


